import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import "./UploadPage.css";
import {
  getBusinessUpdates,
  saveBusinessUpdates,
  getCallouts,
  saveCallouts,
  uploadFile,
  getAccessList,
  getAiPrompts,
  saveAiPrompt,
} from "shared/api/function_data";
import {
  invalidateAfterUpload,
  invalidateBusinessUpdates,
  invalidateCallouts,
} from "shared/lib/queryClient";
import { useUser } from "shared/context/UserContext";

/* ═══════════════════════════════════════════
   Constants
   ═══════════════════════════════════════════ */

const TABS = [
  { id: "file-upload", label: "File Upload", icon: "📄" },
  { id: "business-updates", label: "Business Updates", icon: "📊" },
  { id: "callout", label: "Callout Upload", icon: "📢" },
  { id: "ai-prompt", label: "Slide Context", icon: "📋" },
];

const BU_CARD_FIELDS = [
  { key: "success", title: "Success / Strength", icon: "✅", color: "#10b981" },
  {
    key: "opportunities",
    title: "Opportunities & Incident Reporting",
    icon: "🔍",
    color: "#f59e0b",
  },
  { key: "helpNeeded", title: "Help Needed", icon: "🤝", color: "#ef4444" },
  { key: "nextSteps", title: "Next Steps", icon: "➡️", color: "#6366f1" },
];

const ACCEPTED_FILE_TYPES = ".pdf";
const MAX_FILE_SIZE_MB = 25;

const BU_DEFAULT_KEY = "general";

/* ═══════════════════════════════════════════
   HELPER: Extract sidebar keys with label + key
   ═══════════════════════════════════════════ */

function extractSidebarKeys(sidebarOptions) {
  if (!sidebarOptions) return [];
  const keys = [];
  for (const section of Object.values(sidebarOptions)) {
    if (section.items) {
      section.items.forEach((item) => {
        keys.push({ key: item.key, label: item.label || item.key });
      });
    }
  }
  return keys;
}

/* ═══════════════════════════════════════════
   HOOK: useAccessControl
   ═══════════════════════════════════════════ */

function useAccessControl(functionId, year, userLogin, allSidebarKeys) {
  const [userRole, setUserRole] = useState(null);
  const [allowedItems, setAllowedItems] = useState([]);
  const [accessLoading, setAccessLoading] = useState(true);
  const [accessError, setAccessError] = useState(null);

  useEffect(() => {
    if (!functionId || !year || !userLogin) {
      setAccessLoading(false);
      return;
    }

    let cancelled = false;
    setAccessLoading(true);
    setAccessError(null);

    getAccessList(functionId, year)
      .then((list) => {
        if (cancelled) return;
        const entry = list.find(
          (u) => u.login?.toLowerCase() === userLogin.toLowerCase()
        );
        if (!entry) {
          setUserRole(null);
          setAllowedItems([]);
        } else {
          setUserRole(entry.role);
          setAllowedItems(entry.allowed_items || []);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to fetch access list:", err);
        setAccessError(err.message || "Failed to load access permissions.");
        setUserRole(null);
        setAllowedItems([]);
      })
      .finally(() => {
        if (!cancelled) setAccessLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [functionId, year, userLogin]);

  const filteredSidebarKeys = useMemo(() => {
    if (!userRole) return [];
    if (userRole === "admin") return allSidebarKeys;
    return allSidebarKeys.filter((item) => allowedItems.includes(item.key));
  }, [userRole, allowedItems, allSidebarKeys]);

  return { userRole, allowedItems, filteredSidebarKeys, accessLoading, accessError };
}

/* ═══════════════════════════════════════════
   REUSABLE: Key Selector Component
   ═══════════════════════════════════════════ */

function KeySelector({ value, onChange, sidebarKeys, theme }) {
  return (
    <div className="up-key-selector">
      <label className="up-key-selector__label">
        <span className="up-key-selector__label-icon">🔑</span>
        Select Your Function
      </label>
      <div className="up-key-selector__wrapper">
        <select
          className="up-key-selector__select"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">— Select a function to proceed —</option>
          {sidebarKeys.map((item) => (
            <option key={item.key} value={item.key}>
              {item.label}
            </option>
          ))}
        </select>
        <span className="up-key-selector__chevron">▾</span>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   REUSABLE: Meta Chips
   ═══════════════════════════════════════════ */

function MetaChips({ week, year, functionId, selectedKey, sidebarKeys, extra }) {
  const keyLabel =
    sidebarKeys?.find((s) => s.key === selectedKey)?.label || selectedKey;

  return (
    <div className="up-meta-chips">
      <div className="up-meta-chip">
        <span className="up-meta-chip__icon">📅</span>
        <div className="up-meta-chip__body">
          <span className="up-meta-chip__label">Week</span>
          <span className="up-meta-chip__value">{week}</span>
        </div>
      </div>
      <div className="up-meta-chip">
        <span className="up-meta-chip__icon">📆</span>
        <div className="up-meta-chip__body">
          <span className="up-meta-chip__label">Year</span>
          <span className="up-meta-chip__value">{year}</span>
        </div>
      </div>
      <div className="up-meta-chip">
        <span className="up-meta-chip__icon">⚙️</span>
        <div className="up-meta-chip__body">
          <span className="up-meta-chip__label">Function</span>
          <span className="up-meta-chip__value">{functionId}</span>
        </div>
      </div>
      {selectedKey && (
        <div className="up-meta-chip up-meta-chip--highlight">
          <span className="up-meta-chip__icon">🔑</span>
          <div className="up-meta-chip__body">
            <span className="up-meta-chip__label">Key</span>
            <span className="up-meta-chip__value">{keyLabel}</span>
          </div>
        </div>
      )}
      {extra &&
        extra.map((item, i) => (
          <div key={i} className="up-meta-chip">
            <span className="up-meta-chip__icon">{item.icon}</span>
            <div className="up-meta-chip__body">
              <span className="up-meta-chip__label">{item.label}</span>
              <span className="up-meta-chip__value">{item.value}</span>
            </div>
          </div>
        ))}
    </div>
  );
}

/* ═══════════════════════════════════════════
   REUSABLE: Alert Banner
   ═══════════════════════════════════════════ */

function AlertBanner({ result, onDismiss }) {
  if (!result) return null;
  return (
    <div
      className={`up-alert ${
        result.success ? "up-alert--success" : "up-alert--error"
      }`}
    >
      <span className="up-alert__icon">{result.success ? "✅" : "❌"}</span>
      <span className="up-alert__msg">{result.message}</span>
      {onDismiss && (
        <button className="up-alert__close" onClick={onDismiss}>
          ✕
        </button>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   REUSABLE: Empty State Placeholder
   ═══════════════════════════════════════════ */

function EmptyPlaceholder({ icon, title, subtitle }) {
  return (
    <div className="up-empty">
      <div className="up-empty__icon">{icon}</div>
      <h3 className="up-empty__title">{title}</h3>
      <p className="up-empty__subtitle">{subtitle}</p>
    </div>
  );
}

/* ═══════════════════════════════════════════
   REUSABLE: Role Badge
   ═══════════════════════════════════════════ */

function RoleBadge({ role }) {
  if (!role) return null;
  const isAdmin = role === "admin";
  return (
    <span
      className={`up-role-badge ${
        isAdmin ? "up-role-badge--admin" : "up-role-badge--editor"
      }`}
    >
      {isAdmin ? "🛡️" : "✏️"} {role.charAt(0).toUpperCase() + role.slice(1)}
    </span>
  );
}

/* ═══════════════════════════════════════════
   SUB-COMPONENT: File Upload Tab
   (unchanged)
   ═══════════════════════════════════════════ */

function FileUploadTab({ theme, functionId, week, year, sidebarKeys }) {
  const [selectedKey, setSelectedKey] = useState("");
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (selectedKey && !sidebarKeys.some((k) => k.key === selectedKey)) {
      setSelectedKey("");
      setFile(null);
      setUploadResult(null);
    }
  }, [sidebarKeys, selectedKey]);

  const handleKeyChange = (val) => {
    setSelectedKey(val);
    setUploadResult(null);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  }, []);

  const validateFile = (f) => {
    if (!f) return "No file selected.";
    if (!f.name.toLowerCase().endsWith(".pdf"))
      return "Only PDF files are accepted.";
    if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024)
      return `File size exceeds ${MAX_FILE_SIZE_MB}MB limit.`;
    return null;
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const droppedFile = e.dataTransfer.files?.[0];
    const err = validateFile(droppedFile);
    if (err) {
      setUploadResult({ success: false, message: err });
      return;
    }
    setFile(droppedFile);
    setUploadResult(null);
  }, []);

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    const err = validateFile(selected);
    if (err) {
      setUploadResult({ success: false, message: err });
      return;
    }
    setFile(selected);
    setUploadResult(null);
  };

  const handleRemoveFile = () => {
    setFile(null);
    setUploadResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUpload = async () => {
    if (!selectedKey) {
      setUploadResult({
        success: false,
        message: "Please select a key from the dropdown.",
      });
      return;
    }
    if (!file) {
      setUploadResult({
        success: false,
        message: "Please select a file to upload.",
      });
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      await uploadFile(functionId, year, week, selectedKey, file);
      // Anti-stale: the new PDF (and any AI insight + sidebar status) must
      // refetch next time it's viewed, even if it was cached before.
      invalidateAfterUpload(queryClient, functionId, year, week, selectedKey);
      setUploadResult({
        success: true,
        message: `File uploaded successfully! Saved as ${selectedKey}_wbr_${week}.pdf`,
      });
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      setUploadResult({
        success: false,
        message: err.message || "Upload failed.",
      });
    } finally {
      setUploading(false);
    }
  };

  const canUpload = selectedKey && file && !uploading;
  const targetFileName = selectedKey ? `${selectedKey}_wbr_${week}.pdf` : "";

  return (
    <div className="up-tab-panel">
      <div className="up-panel-header">
        <div className="up-panel-header__text">
          <h2 className="up-panel-title">Upload PDF File</h2>
          <p className="up-panel-subtitle">
            Select a metric key, attach a PDF file, and upload it for the
            selected week and year.
          </p>
        </div>
      </div>

      <div className="up-panel-body">
        <KeySelector
          value={selectedKey}
          onChange={handleKeyChange}
          sidebarKeys={sidebarKeys}
          theme={theme}
        />

        {selectedKey && (
          <>
            <MetaChips
              week={week}
              year={year}
              functionId={functionId}
              selectedKey={selectedKey}
              sidebarKeys={sidebarKeys}
              extra={[
                { icon: "📁", label: "Target File", value: targetFileName },
              ]}
            />

            <div
              className={`up-dropzone ${
                dragActive ? "up-dropzone--active" : ""
              } ${file ? "up-dropzone--has-file" : ""}`}
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              onClick={() => !file && fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_FILE_TYPES}
                onChange={handleFileChange}
                className="up-dropzone__input"
              />

              {file ? (
                <div className="up-dropzone__preview">
                  <div className="up-dropzone__file-icon">
                    <svg viewBox="0 0 24 24" width="40" height="40">
                      <path
                        fill="currentColor"
                        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4zm-3 9h4v2h-4v-2zm0 3h4v2h-4v-2zm-2-3h1v2H8v-2zm0 3h1v2H8v-2z"
                      />
                    </svg>
                  </div>
                  <div className="up-dropzone__file-info">
                    <span className="up-dropzone__file-name">{file.name}</span>
                    <span className="up-dropzone__file-size">
                      {(file.size / (1024 * 1024)).toFixed(2)} MB
                    </span>
                    <span className="up-dropzone__file-target">
                      → Will be saved as <strong>{targetFileName}</strong>
                    </span>
                  </div>
                  <button
                    className="up-dropzone__remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFile();
                    }}
                    title="Remove file"
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18">
                      <path
                        fill="currentColor"
                        d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                      />
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="up-dropzone__empty">
                  <div className="up-dropzone__cloud-icon">
                    <svg viewBox="0 0 24 24" width="48" height="48">
                      <path
                        fill="currentColor"
                        d="M19.35 10.04A7.49 7.49 0 0 0 12 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 0 0 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"
                      />
                    </svg>
                  </div>
                  <p className="up-dropzone__text">
                    <strong>Click to browse</strong> or drag & drop your file
                    here
                  </p>
                  <p className="up-dropzone__hint">
                    PDF files only &middot; Max {MAX_FILE_SIZE_MB}MB
                  </p>
                </div>
              )}
            </div>

            <AlertBanner
              result={uploadResult}
              onDismiss={() => setUploadResult(null)}
            />

            <div className="up-panel-actions">
              <button
                className={`up-btn up-btn--primary up-btn--lg ${
                  uploading ? "up-btn--loading" : ""
                }`}
                onClick={handleUpload}
                disabled={!canUpload}
              >
                {uploading ? (
                  <>
                    <span className="up-btn__spinner" />
                    Uploading…
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" width="18" height="18">
                      <path
                        fill="currentColor"
                        d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z"
                      />
                    </svg>
                    Upload File
                  </>
                )}
              </button>
            </div>
          </>
        )}

        {!selectedKey && (
          <EmptyPlaceholder
            icon="📂"
            title="No Key Selected"
            subtitle="Choose a metric key from the dropdown above to start uploading your PDF file."
          />
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   SUB-COMPONENT: Business Updates Tab
   ── NO key selector ──
   ── Segments load automatically ──
   ── Each segment tracks updatedBy / updatedAt ──
   ═══════════════════════════════════════════ */

function BusinessUpdatesTab({ theme, functionId, week, year, userLogin }) {
  const queryClient = useQueryClient();
  const [segments, setSegments] = useState([]);
  const [showAddSegment, setShowAddSegment] = useState(false);
  const [newSegmentForm, setNewSegmentForm] = useState({
    title: "",
    success: "",
    opportunities: "",
    helpNeeded: "",
    nextSteps: "",
  });

  const [editingIdx, setEditingIdx] = useState(null);
  const [editBuffer, setEditBuffer] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [collapsedSegments, setCollapsedSegments] = useState({});
  const [loadingSegments, setLoadingSegments] = useState(false);

  /* ── Load segments on mount / when week-year changes ── */
  useEffect(() => {
    let cancelled = false;
    setLoadingSegments(true);
    setSaveResult(null);
    setEditingIdx(null);
    setShowAddSegment(false);

    getBusinessUpdates(functionId, year, week, BU_DEFAULT_KEY)
      .then((data) => {
        if (cancelled) return;
        setSegments(data.segments || []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load business updates:", err);
        setSegments([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSegments(false);
      });

    return () => {
      cancelled = true;
    };
  }, [functionId, year, week]);

  const toggleCollapse = (idx) =>
    setCollapsedSegments((p) => ({ ...p, [idx]: !p[idx] }));

  /* ── Add segment (stamps updatedBy + updatedAt) ── */
  const handleAddSegment = () => {
    if (!newSegmentForm.title.trim()) return;
    const now = new Date().toISOString();
    setSegments((prev) => [
      ...prev,
      {
        ...newSegmentForm,
        updatedBy: userLogin,
        updatedAt: now,
      },
    ]);
    setNewSegmentForm({
      title: "",
      success: "",
      opportunities: "",
      helpNeeded: "",
      nextSteps: "",
    });
    setShowAddSegment(false);
  };

  const handleDeleteSegment = (idx) => {
    setSegments((prev) => prev.filter((_, i) => i !== idx));
    if (editingIdx === idx) {
      setEditingIdx(null);
      setEditBuffer({});
    }
  };

  const startEdit = (idx) => {
    setEditingIdx(idx);
    setEditBuffer({ ...segments[idx] });
  };

  const cancelEdit = () => {
    setEditingIdx(null);
    setEditBuffer({});
  };

  /* ── Save inline edit (stamps updatedBy + updatedAt) ── */
  const saveEdit = () => {
    const now = new Date().toISOString();
    setSegments((prev) =>
      prev.map((s, i) =>
        i === editingIdx
          ? { ...editBuffer, updatedBy: userLogin, updatedAt: now }
          : s
      )
    );
    setEditingIdx(null);
    setEditBuffer({});
  };

  /* ── Persist to server ── */
  const handleSaveAll = async () => {
    setSaving(true);
    setSaveResult(null);

    try {
      await saveBusinessUpdates(
        functionId,
        year,
        week,
        BU_DEFAULT_KEY,
        segments
      );
      invalidateBusinessUpdates(queryClient, functionId, year, week, BU_DEFAULT_KEY);
      setSaveResult({
        success: true,
        message: "Business updates saved successfully!",
      });
    } catch (err) {
      setSaveResult({
        success: false,
        message: err.message || "Save failed.",
      });
    } finally {
      setSaving(false);
    }
  };

  /* ── Helper to format timestamp ── */
  const formatTime = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="up-tab-panel">
      <div className="up-panel-header">
        <div className="up-panel-header__text">
          <h2 className="up-panel-title">Business Updates</h2>
          <p className="up-panel-subtitle">
            Create, edit, and manage business update segments for Week {week},{" "}
            {year}.
          </p>
        </div>
      </div>

      <div className="up-panel-body">
        {/* ── Context chips (no key chip) ── */}
        <MetaChips
          week={week}
          year={year}
          functionId={functionId}
          selectedKey={null}
          sidebarKeys={[]}
          extra={[{ icon: "📋", label: "Segments", value: segments.length }]}
        />

        {loadingSegments ? (
          <div className="up-loader">
            <span className="up-btn__spinner up-btn__spinner--dark" />
            <span>Loading existing segments…</span>
          </div>
        ) : (
          <div className="up-segments">
            {segments.map((segment, idx) => (
              <div
                key={idx}
                className={`up-segment ${
                  editingIdx === idx ? "up-segment--editing" : ""
                }`}
              >
                <div
                  className="up-segment__header"
                  onClick={() => editingIdx !== idx && toggleCollapse(idx)}
                >
                  <div className="up-segment__header-left">
                    <span className="up-segment__chevron">
                      {collapsedSegments[idx] ? (
                        <svg viewBox="0 0 24 24" width="18" height="18">
                          <path
                            fill="currentColor"
                            d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"
                          />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" width="18" height="18">
                          <path
                            fill="currentColor"
                            d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z"
                          />
                        </svg>
                      )}
                    </span>
                    <h4 className="up-segment__title">{segment.title}</h4>
                    <span className="up-segment__badge">
                      {
                        BU_CARD_FIELDS.filter((f) => segment[f.key]?.trim())
                          .length
                      }
                      /{BU_CARD_FIELDS.length}
                    </span>
                  </div>

                  {/* ── Updated-by tag ── */}
                  {segment.updatedBy && (
                    <span
                      className="up-segment__updated-by"
                      onClick={(e) => e.stopPropagation()}
                    >
                      ✏️ {segment.updatedBy}
                      {segment.updatedAt && ` · ${formatTime(segment.updatedAt)}`}
                    </span>
                  )}

                  <div
                    className="up-segment__header-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {editingIdx === idx ? (
                      <>
                        <button
                          className="up-icon-btn up-icon-btn--save"
                          onClick={saveEdit}
                          title="Save"
                        >
                          <svg viewBox="0 0 24 24" width="16" height="16">
                            <path
                              fill="currentColor"
                              d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
                            />
                          </svg>
                        </button>
                        <button
                          className="up-icon-btn up-icon-btn--cancel"
                          onClick={cancelEdit}
                          title="Cancel"
                        >
                          <svg viewBox="0 0 24 24" width="16" height="16">
                            <path
                              fill="currentColor"
                              d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                            />
                          </svg>
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="up-icon-btn up-icon-btn--edit"
                          onClick={() => startEdit(idx)}
                          title="Edit"
                        >
                          <svg viewBox="0 0 24 24" width="16" height="16">
                            <path
                              fill="currentColor"
                              d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z"
                            />
                          </svg>
                        </button>
                        <button
                          className="up-icon-btn up-icon-btn--delete"
                          onClick={() => handleDeleteSegment(idx)}
                          title="Delete"
                        >
                          <svg viewBox="0 0 24 24" width="16" height="16">
                            <path
                              fill="currentColor"
                              d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
                            />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {!collapsedSegments[idx] && (
                  <div className="up-segment__body">
                    {editingIdx === idx ? (
                      <div className="up-segment__edit-form">
                        <div className="up-field">
                          <label className="up-field__label">
                            Segment Title
                          </label>
                          <input
                            type="text"
                            className="up-field__input"
                            value={editBuffer.title || ""}
                            onChange={(e) =>
                              setEditBuffer({
                                ...editBuffer,
                                title: e.target.value,
                              })
                            }
                          />
                        </div>
                        {BU_CARD_FIELDS.map((field) => (
                          <div key={field.key} className="up-field">
                            <label className="up-field__label">
                              <span
                                className="up-field__dot"
                                style={{ background: field.color }}
                              />
                              {field.title}
                            </label>
                            <textarea
                              className="up-field__textarea"
                              rows="3"
                              value={editBuffer[field.key] || ""}
                              onChange={(e) =>
                                setEditBuffer({
                                  ...editBuffer,
                                  [field.key]: e.target.value,
                                })
                              }
                              placeholder={`Enter ${field.title.toLowerCase()}…`}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="up-segment__cards">
                        {BU_CARD_FIELDS.map((field) => {
                          const content = segment[field.key];
                          if (!content?.trim()) return null;
                          return (
                            <div
                              key={field.key}
                              className="up-bu-card"
                              style={{ borderLeftColor: field.color }}
                            >
                              <h6 className="up-bu-card__title">
                                <span
                                  className="up-bu-card__dot"
                                  style={{ background: field.color }}
                                />
                                {field.title}
                              </h6>
                              <div className="up-bu-card__content">
                                {content.split("\n").map((line, i) => (
                                  <p key={i}>{line}</p>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                        {BU_CARD_FIELDS.every(
                          (f) => !segment[f.key]?.trim()
                        ) && (
                          <p className="up-segment__empty-note">
                            No content yet — click Edit to add updates.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* ── Add segment form ── */}
            {showAddSegment ? (
              <div className="up-add-form">
                <div className="up-add-form__header">
                  <h4 className="up-add-form__title">Add New Segment</h4>
                  <button
                    className="up-icon-btn up-icon-btn--cancel"
                    onClick={() => setShowAddSegment(false)}
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16">
                      <path
                        fill="currentColor"
                        d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                      />
                    </svg>
                  </button>
                </div>
                <div className="up-add-form__body">
                  <div className="up-field">
                    <label className="up-field__label">Segment Title *</label>
                    <input
                      type="text"
                      className="up-field__input"
                      placeholder="e.g. VRMO, FP, Quality Assurance…"
                      value={newSegmentForm.title}
                      onChange={(e) =>
                        setNewSegmentForm({
                          ...newSegmentForm,
                          title: e.target.value,
                        })
                      }
                    />
                  </div>
                  {BU_CARD_FIELDS.map((field) => (
                    <div key={field.key} className="up-field">
                      <label className="up-field__label">
                        <span
                          className="up-field__dot"
                          style={{ background: field.color }}
                        />
                        {field.title}
                      </label>
                      <textarea
                        className="up-field__textarea"
                        rows="3"
                        placeholder={`Enter ${field.title.toLowerCase()}…`}
                        value={newSegmentForm[field.key] || ""}
                        onChange={(e) =>
                          setNewSegmentForm({
                            ...newSegmentForm,
                            [field.key]: e.target.value,
                          })
                        }
                      />
                    </div>
                  ))}
                </div>
                <div className="up-add-form__footer">
                  <span className="up-add-form__author">
                    Will be saved as: <strong>{userLogin}</strong>
                  </span>
                  <div>
                    <button
                      className="up-btn up-btn--ghost"
                      onClick={() => setShowAddSegment(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="up-btn up-btn--primary"
                      onClick={handleAddSegment}
                      disabled={!newSegmentForm.title.trim()}
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16">
                        <path
                          fill="currentColor"
                          d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"
                        />
                      </svg>
                      Add Segment
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button
                className="up-btn up-btn--dashed up-btn--block"
                onClick={() => setShowAddSegment(true)}
              >
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <path
                    fill="currentColor"
                    d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"
                  />
                </svg>
                Add New Segment
              </button>
            )}
          </div>
        )}

        <AlertBanner
          result={saveResult}
          onDismiss={() => setSaveResult(null)}
        />

        {segments.length > 0 && (
          <div className="up-panel-actions">
            <button
              className={`up-btn up-btn--primary up-btn--lg ${
                saving ? "up-btn--loading" : ""
              }`}
              onClick={handleSaveAll}
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="up-btn__spinner" />
                  Saving…
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" width="18" height="18">
                    <path
                      fill="currentColor"
                      d="M17 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7l-4-4zm-5 16a3 3 0 1 1 0-6 3 3 0 0 1 0 6zm3-10H5V5h10v4z"
                    />
                  </svg>
                  Save All Updates
                </>
              )}
            </button>
          </div>
        )}

        {!loadingSegments && segments.length === 0 && (
          <EmptyPlaceholder
            icon="📊"
            title="No Segments Yet"
            subtitle="Click 'Add New Segment' above to create your first business update."
          />
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   SUB-COMPONENT: Callout Upload Tab
   (unchanged)
   ═══════════════════════════════════════════ */

function CalloutUploadTab({ theme, functionId, week, year, sidebarKeys }) {
  const queryClient = useQueryClient();
  const [selectedKey, setSelectedKey] = useState("");
  const [callouts, setCallouts] = useState([]);
  const [newCalloutText, setNewCalloutText] = useState("");
  const [editingIdx, setEditingIdx] = useState(null);
  const [editText, setEditText] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [loadingCallouts, setLoadingCallouts] = useState(false);

  useEffect(() => {
    if (selectedKey && !sidebarKeys.some((k) => k.key === selectedKey)) {
      setSelectedKey("");
      setCallouts([]);
      setSaveResult(null);
    }
  }, [sidebarKeys, selectedKey]);

  /* ── Persist helper — saves given array to server ── */
  const persistCallouts = useCallback(
    async (updatedCallouts) => {
      if (!selectedKey) return;
      setSaving(true);
      setSaveResult(null);
      try {
        await saveCallouts(functionId, year, week, selectedKey, updatedCallouts);
        // Callout appears both in the upload editor and on the report page.
        invalidateCallouts(queryClient, functionId, year, week, selectedKey);
        setSaveResult({ success: true, message: "Saved successfully!" });
      } catch (err) {
        setSaveResult({
          success: false,
          message: err.message || "Save failed.",
        });
      } finally {
        setSaving(false);
      }
    },
    [functionId, year, week, selectedKey, queryClient],
  );

  /* ── Auto-dismiss success alerts after 3s ── */
  useEffect(() => {
    if (saveResult?.success) {
      const timer = setTimeout(() => setSaveResult(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [saveResult]);

  const handleKeyChange = async (key) => {
    setSelectedKey(key);
    setSaveResult(null);
    setEditingIdx(null);
    setCallouts([]);
    setNewCalloutText("");

    if (!key) return;

    setLoadingCallouts(true);
    try {
      const data = await getCallouts(functionId, year, week, key);
      setCallouts(data.callouts || []);
    } catch (err) {
      console.error("Failed to load callouts:", err);
      setCallouts([]);
    } finally {
      setLoadingCallouts(false);
    }
  };

  /* ── Add + auto-save ── */
  const handleAddCallout = async () => {
    if (!newCalloutText.trim()) return;
    const updated = [
      ...callouts,
      { text: newCalloutText.trim(), createdAt: new Date().toISOString() },
    ];
    setCallouts(updated);
    setNewCalloutText("");
    await persistCallouts(updated);
  };

  /* ── Delete + auto-save ── */
  const handleDeleteCallout = async (idx) => {
    const updated = callouts.filter((_, i) => i !== idx);
    setCallouts(updated);
    if (editingIdx === idx) {
      setEditingIdx(null);
      setEditText("");
    }
    await persistCallouts(updated);
  };

  const startEdit = (idx) => {
    setEditingIdx(idx);
    setEditText(callouts[idx].text);
  };

  const cancelEdit = () => {
    setEditingIdx(null);
    setEditText("");
  };

  /* ── Save edit + auto-save ── */
  const saveEditCallout = async () => {
    if (!editText.trim()) return;
    const updated = callouts.map((c, i) =>
      i === editingIdx ? { ...c, text: editText.trim() } : c,
    );
    setCallouts(updated);
    setEditingIdx(null);
    setEditText("");
    await persistCallouts(updated);
  };

  return (
    <div className="up-tab-panel">
      <div className="up-panel-header">
        <div className="up-panel-header__text">
          <h2 className="up-panel-title">Callout Statements</h2>
          <p className="up-panel-subtitle">
            Add, edit, and manage callout text statements for each metric key.
            Changes are saved automatically.
          </p>
        </div>
      </div>

      <div className="up-panel-body">
        <KeySelector
          value={selectedKey}
          onChange={handleKeyChange}
          sidebarKeys={sidebarKeys}
          theme={theme}
        />

        {selectedKey && (
          <>
            <MetaChips
              week={week}
              year={year}
              functionId={functionId}
              selectedKey={selectedKey}
              sidebarKeys={sidebarKeys}
              extra={[
                { icon: "💬", label: "Callouts", value: callouts.length },
              ]}
            />

            {loadingCallouts ? (
              <div className="up-loader">
                <span className="up-btn__spinner up-btn__spinner--dark" />
                <span>Loading callouts…</span>
              </div>
            ) : (
              <div className="up-callouts">
                {/* ── Existing callouts ── */}
                {callouts.map((callout, idx) => (
                  <div
                    key={idx}
                    className={`up-callout ${
                      editingIdx === idx ? "up-callout--editing" : ""
                    }`}
                  >
                    {editingIdx === idx ? (
                      <div className="up-callout__edit">
                        <textarea
                          className="up-field__textarea"
                          rows="3"
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          placeholder="Enter callout text…"
                          autoFocus
                        />
                        <div className="up-callout__edit-actions">
                          <button
                            className="up-icon-btn up-icon-btn--save"
                            onClick={saveEditCallout}
                            disabled={!editText.trim() || saving}
                            title="Save"
                          >
                            <svg viewBox="0 0 24 24" width="16" height="16">
                              <path
                                fill="currentColor"
                                d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
                              />
                            </svg>
                          </button>
                          <button
                            className="up-icon-btn up-icon-btn--cancel"
                            onClick={cancelEdit}
                            title="Cancel"
                          >
                            <svg viewBox="0 0 24 24" width="16" height="16">
                              <path
                                fill="currentColor"
                                d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="up-callout__display">
                        <div className="up-callout__number">{idx + 1}</div>
                        <p className="up-callout__text">{callout.text}</p>
                        <div className="up-callout__actions">
                          <button
                            className="up-icon-btn up-icon-btn--edit"
                            onClick={() => startEdit(idx)}
                            title="Edit"
                            disabled={saving}
                          >
                            <svg viewBox="0 0 24 24" width="14" height="14">
                              <path
                                fill="currentColor"
                                d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z"
                              />
                            </svg>
                          </button>
                          <button
                            className="up-icon-btn up-icon-btn--delete"
                            onClick={() => handleDeleteCallout(idx)}
                            title="Delete"
                            disabled={saving}
                          >
                            <svg viewBox="0 0 24 24" width="14" height="14">
                              <path
                                fill="currentColor"
                                d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* ── Add new callout (single-step: type + click = saved) ── */}
                <div className="up-callout-add">
                  <div className="up-callout-add__input-row">
                    <textarea
                      className="up-field__textarea"
                      rows="3"
                      placeholder="Type a new callout statement…"
                      value={newCalloutText}
                      onChange={(e) => setNewCalloutText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && e.ctrlKey) handleAddCallout();
                      }}
                      disabled={saving}
                    />
                  </div>
                  <div className="up-callout-add__footer">
                    <span className="up-callout-add__hint">
                      Ctrl + Enter to add &middot; Auto-saves on every action
                    </span>
                    <button
                      className={`up-btn up-btn--primary ${
                        saving ? "up-btn--loading" : ""
                      }`}
                      onClick={handleAddCallout}
                      disabled={!newCalloutText.trim() || saving}
                    >
                      {saving ? (
                        <>
                          <span className="up-btn__spinner" />
                          Saving…
                        </>
                      ) : (
                        <>
                          <svg viewBox="0 0 24 24" width="16" height="16">
                            <path
                              fill="currentColor"
                              d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"
                            />
                          </svg>
                          Add Callout
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <AlertBanner
              result={saveResult}
              onDismiss={() => setSaveResult(null)}
            />
          </>
        )}

        {!selectedKey && (
          <EmptyPlaceholder
            icon="📢"
            title="No Key Selected"
            subtitle="Choose a metric key to add and manage callout statements."
          />
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   SUB-COMPONENT: AI Prompt Tab
   Contributors set their prompt per key
   ═══════════════════════════════════════════ */

function AiPromptTab({ theme, functionId, week, year, sidebarKeys, userLogin }) {
  const [selectedKey, setSelectedKey] = useState("");
  const [aiPrompts, setAiPrompts] = useState({});
  const [promptText, setPromptText] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  // Load all AI prompts on mount
  useEffect(() => {
    if (!functionId || !year) return;
    setLoading(true);
    getAiPrompts(functionId, year)
      .then((data) => setAiPrompts(data || {}))
      .catch(() => setAiPrompts({}))
      .finally(() => setLoading(false));
  }, [functionId, year]);

  // Filter to only show keys that have AI enabled
  const enabledKeys = sidebarKeys.filter((item) => aiPrompts[item.key]?.enabled);

  // When key changes, load its prompt
  useEffect(() => {
    if (selectedKey && aiPrompts[selectedKey]) {
      setPromptText(aiPrompts[selectedKey].prompt || "");
    } else {
      setPromptText("");
    }
    setResult(null);
  }, [selectedKey, aiPrompts]);

  const handleSave = async () => {
    if (!selectedKey) return;
    setSaving(true);
    setResult(null);
    try {
      await saveAiPrompt(functionId, year, selectedKey, true, promptText, userLogin);
      setAiPrompts((prev) => ({
        ...prev,
        [selectedKey]: { ...prev[selectedKey], prompt: promptText, last_updated_by: userLogin },
      }));
      setResult({ success: true, message: "AI prompt saved successfully!" });
    } catch (err) {
      setResult({ success: false, message: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="up-tab-panel">
        <div className="up-loader">
          <span className="up-btn__spinner up-btn__spinner--dark" />
          <span>Loading AI settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="up-tab-panel">
      <div className="up-panel-header">
        <div className="up-panel-header__text">
          <h2 className="up-panel-title">📋 Slide Context — Describe Your Metrics</h2>
          <p className="up-panel-subtitle">
            Help AI understand your report by describing the metrics, goals, and what to focus on. Set once, used every week.
          </p>
        </div>
      </div>

      <div className="up-panel-body">
        {enabledKeys.length === 0 ? (
          <EmptyPlaceholder
            icon="📋"
            title="Slide Context Not Enabled"
            subtitle="No report keys have AI insights enabled. Ask your admin to enable AI for your slides in the Admin → AI Insights tab."
          />
        ) : (
          <>
            <KeySelector
              value={selectedKey}
              onChange={(val) => setSelectedKey(val)}
              sidebarKeys={enabledKeys}
              theme={theme}
            />

            {selectedKey && (
              <>
                <div style={{
                  padding: "12px 16px", borderRadius: "8px", marginBottom: "16px",
                  background: "#f0f4ff", border: "1px solid #c7d2fe", fontSize: "12px",
                  lineHeight: 1.6, color: "#4338ca",
                }}>
                  <strong>💡 Tips:</strong> Describe the metrics on your slide, their exact goals (from the Goal column),
                  and what the AI should focus on. Example: "SLA goal is 90% (from row 1). Productive Time goal is 83%.
                  Highlight any entity missing SLA. Show WoW trends."
                </div>

                <div style={{ marginBottom: "16px" }}>
                  <label style={{
                    display: "block", fontSize: "12px", fontWeight: 700,
                    textTransform: "uppercase", color: "#6b7280", marginBottom: "6px",
                  }}>
                    Slide Context for "{enabledKeys.find((k) => k.key === selectedKey)?.label || selectedKey}"
                  </label>
                  <textarea
                    value={promptText}
                    onChange={(e) => setPromptText(e.target.value)}
                    placeholder="Describe your slide's metrics and goals. E.g.:\n\n- Overall SLA: goal 90% (row 1)\n- Productive Time: goal 83% (row 15)\n- Idle Time: goal <4% (row 17)\n- Entities: APE/APUK, ACCU, ASVBL, API\n\nFocus on: which entities missed SLA, WoW trends, shrinkage impact..."
                    rows={10}
                    style={{
                      width: "100%", padding: "12px 14px", fontSize: "13px",
                      border: "1px solid #d1d5db", borderRadius: "8px",
                      fontFamily: "inherit", resize: "vertical", lineHeight: 1.6,
                      outline: "none", boxSizing: "border-box",
                    }}
                  />
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: "6px" }}>
                    <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                      {aiPrompts[selectedKey]?.last_updated_by
                        ? `Last set by: ${aiPrompts[selectedKey].last_updated_by}`
                        : "No prompt set yet"}
                    </span>
                    <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                      {promptText.length} chars
                    </span>
                  </div>
                </div>

                {result && (
                  <AlertBanner result={result} onDismiss={() => setResult(null)} />
                )}

                <div className="up-panel-actions">
                  <button
                    className={`up-btn up-btn--primary up-btn--lg ${saving ? "up-btn--loading" : ""}`}
                    onClick={handleSave}
                    disabled={saving || !promptText.trim()}
                  >
                    {saving ? (
                      <><span className="up-btn__spinner" /> Saving...</>
                    ) : (
                      <>💾 Save Context</>
                    )}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   MAIN EXPORT: UploadPage
   ═══════════════════════════════════════════ */

export default function UploadPage({
  theme = "light",
  function_id,
  week,
  year,
  functionConfig,
}) {
  const user = useUser();
  const userLogin = user?.userId || "";

  const [activeTab, setActiveTab] = useState(TABS[0].id);

  const allSidebarKeys = useMemo(
    () => extractSidebarKeys(functionConfig?.function_sidebar),
    [functionConfig]
  );

  const {
    userRole,
    filteredSidebarKeys,
    accessLoading,
    accessError,
  } = useAccessControl(function_id, year, userLogin, allSidebarKeys);

  if (!userLogin) {
    return (
      <div className={`up-root ${theme === "dark" ? "up-root--dark" : ""}`}>
        <EmptyPlaceholder
          icon="⏳"
          title="Loading User"
          subtitle="Waiting for user information to load…"
        />
      </div>
    );
  }

  if (accessLoading) {
    return (
      <div className={`up-root ${theme === "dark" ? "up-root--dark" : ""}`}>
        <div className="up-access-loading">
          <span className="up-btn__spinner up-btn__spinner--dark" />
          <p>Loading your access permissions…</p>
        </div>
      </div>
    );
  }

  if (accessError) {
    return (
      <div className={`up-root ${theme === "dark" ? "up-root--dark" : ""}`}>
        <div className="up-access-error">
          <AlertBanner
            result={{ success: false, message: `Access error: ${accessError}` }}
          />
        </div>
      </div>
    );
  }

  if (!userRole) {
    return (
      <div className={`up-root ${theme === "dark" ? "up-root--dark" : ""}`}>
        <EmptyPlaceholder
          icon="🔒"
          title="No Access"
          subtitle={`Your login "${userLogin}" does not have upload permissions for this function. Please contact an admin.`}
        />
      </div>
    );
  }

  if (userRole === "editor" && filteredSidebarKeys.length === 0) {
    return (
      <div className={`up-root ${theme === "dark" ? "up-root--dark" : ""}`}>
        <EmptyPlaceholder
          icon="📭"
          title="No Items Assigned"
          subtitle="You have editor access but no metric keys have been assigned to you yet. Please contact an admin."
        />
      </div>
    );
  }

  return (
    <div className={`up-root ${theme === "dark" ? "up-root--dark" : ""}`}>
      {/* ─── Role indicator bar ─── */}
      <div className="up-role-bar">
        <span className="up-role-bar__user">
          👤 Logged in as <strong>{userLogin}</strong>
        </span>
        <RoleBadge role={userRole} />
        {userRole === "editor" && (
          <span className="up-role-bar__hint">
            You can access {filteredSidebarKeys.length} of{" "}
            {allSidebarKeys.length} keys
          </span>
        )}
      </div>

      <div className="up-tabbar">
        <div className="up-tabbar__inner">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              className={`up-tabbar__tab ${
                activeTab === tab.id ? "up-tabbar__tab--active" : ""
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="up-tabbar__icon">{tab.icon}</span>
              <span className="up-tabbar__label">{tab.label}</span>
              {activeTab === tab.id && (
                <span className="up-tabbar__indicator" />
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="up-body">
        {activeTab === "file-upload" && (
          <FileUploadTab
            theme={theme}
            functionId={function_id}
            week={week}
            year={year}
            sidebarKeys={filteredSidebarKeys}
          />
        )}
        {activeTab === "business-updates" && (
          <BusinessUpdatesTab
            theme={theme}
            functionId={function_id}
            week={week}
            year={year}
            userLogin={userLogin}
          />
        )}
        {activeTab === "callout" && (
          <CalloutUploadTab
            theme={theme}
            functionId={function_id}
            week={week}
            year={year}
            sidebarKeys={filteredSidebarKeys}
          />
        )}
        {activeTab === "ai-prompt" && (
          <AiPromptTab
            theme={theme}
            functionId={function_id}
            week={week}
            year={year}
            sidebarKeys={filteredSidebarKeys}
            userLogin={userLogin}
          />
        )}
      </div>
    </div>
  );
}